<template>
  <b-modal
    ref="modal"
    :title="$t('component.descriptionConcurrenceModal.title')"
    centered
    modal-class="description-concurrence-modal"
    @hidden="$emit('hide')"
  >
    <b-row class="px-3">
      <b-col v-if="currentDescription">
        <b-row class="mb-2">
          <b-col class="d-flex justify-content-end align-items-center">
            <b-button
              block
              size="sm"
              class="d-flex justify-content-between align-items-center px-3 btn__outline"
              @click="applyChangesFromDescription(currentDescription)"
            >
              {{ $t('component.descriptionConcurrenceModal.acceptCurrentVersion') }}
              <i
                class="far fa-arrow-right ml-2"
                aria-hidden="true"
              />
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <vt-rich-text-editor
              input-id="descriptionDescriptionInput"
              class="mt-1 descriptionText preview"
              :value="currentDescription.text"
              :disabled="true"
            />
          </b-col>
        </b-row>
        <b-row class="user-information mx-0">
          <b-col cols="6">
            <div class="d-flex flex-column align-items-center">
              <small>{{ $t('component.descriptionConcurrenceModal.user') }}</small>
              <div class="truncate text-center">
                <span>{{ currentConcurrencyUpdatedBy.fullName }}</span>
              </div>
            </div>
          </b-col>
          <b-col
            class="px-0 d-flex align-items-center"
            cols="auto"
          >
            <div class="detail-line" />
          </b-col>
          <b-col>
            <div class="d-flex flex-column align-items-center">
              <small>{{ $t('component.descriptionConcurrenceModal.updateDate') }}</small>
              <span>{{ modifiedDescription.originalConcurrencyUpdatedAt | datetime }}</span>
            </div>
          </b-col>
        </b-row>
      </b-col>
      <b-col v-if="modifiedDescription">
        <b-row class="mb-2">
          <b-col class="d-flex justify-content-end align-items-center">
            <b-button
              block
              size="sm"
              variant="outline-primary"
              class="d-flex justify-content-between align-items-center px-3"
              @click="applyChangesFromDescription(modifiedDescription)"
            >
              {{ $t('component.descriptionConcurrenceModal.acceptYourChanges') }}
              <i
                class="far fa-arrow-right ml-2"
                aria-hidden="true"
              />
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <vt-rich-text-editor
              input-id="descriptionDescriptionInput"
              class="mt-1 descriptionText"
              :value="modifiedDescription.text"
              :disabled="true"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="5">
        <b-row>
          <b-col>
            <DescriptionEditor
              ref="descriptionEditor"
              name="concurrenceDescriptionEditor"
              class="concurrenceDescriptionEditor"
              :laboratory-id="laboratoryId"
              :description-title="$t('component.descriptionConcurrenceModal.results')"
              :description-service="descriptionService"
              :parent-entity-ids-array="parentEntityIdsArray"
              :root-filename-for-audio-description="rootFilenameForAudioDescription"
              :attachment-type-id-for-description-audio="attachmentTypeIdForDescriptionAudio"
              :attachment-service-for-audio-attachments="attachmentServiceForAudioAttachments"
              :case-id="caseId"
              :sample-id="sampleId"
              :attachment-service="attachmentService"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="_hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            size="sm"
            class="btn--primary px-4"
            @click="_apply"
          >
            {{ $t('global.apply') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { cloneDeep } from 'lodash'

export default {
    name: 'DescriptionConcurrenceModal',
    components: {
        DescriptionEditor: () => import('@/components/description/DescriptionEditor')
    },
    props: {
        laboratoryId: { type: Number, required: true },
        descriptionService: { type: Object, required: true },
        parentEntityIdsArray: { type: Array, required: true },
        rootFilenameForAudioDescription: { type: String, required: true },
        attachmentTypeIdForDescriptionAudio: { type: Number, required: true },
        attachmentServiceForAudioAttachments: { type: Object, required: true },
        caseId: { type: Number, required: false },
        sampleId: { type: Number, required: false },
        attachmentService: { type: Object }
    },
    data() {
        return {
            currentDescription: null,
            modifiedDescription: null,
            currentConcurrencyUpdatedBy: null,
            callback: null
        }
    },
    methods: {
        _show() {
            this.$refs.modal.show()
        },
        _hide() {
            this.$refs.modal.hide()
        },
        async _apply() {
            let saveSuccessful = true
            if (this.$refs.descriptionEditor.hasBeenChanged()) {
                saveSuccessful = await this.$refs.descriptionEditor.save()
            } else {
                this.showToastSuccessGlobal()
            }
            this._hide()
            this.currentDescription = null
            this.modifiedDescription = null
            if (saveSuccessful) {
                this.callback()
            }
        },
        async applyChangesFromDescription(description) {
            await this.$refs.descriptionEditor.setDescriptionManually(description)
        },
        openModalToSolveConflict(currentDescription, modifiedDescription, currentConcurrencyUpdatedBy, callback) {
            this.currentDescription = cloneDeep(currentDescription)
            this.modifiedDescription = cloneDeep(modifiedDescription)
            this.currentConcurrencyUpdatedBy = cloneDeep(currentConcurrencyUpdatedBy)
            this.modifiedDescription.originalConcurrencyUpdatedAt = this.currentDescription.originalConcurrencyUpdatedAt
            this.callback = callback
            this.$nextTick(() => this.$refs.descriptionEditor?.loadData())
            this._show()
        }
    }
}
</script>

<style lang="scss">
.description-concurrence-modal .detail-line{
  width:1px;
  height: 25px;
  background:vt-color(base-text, .7);
}
.description-concurrence-modal .truncate{
  white-space:nowrap;
  overflow:hidden;
  text-overflow: ellipsis;
  width:100%;
}
.description-concurrence-modal .modal-dialog {
  max-width: 85%;
}
.description-concurrence-modal .user-information{
  border-color: #ccc;
  border-style: solid;
  border-width: 0 1px 1px 1px;
  padding: 3px 0;
}
.description-concurrence-modal .user-information small{
  text-transform: uppercase;
}
.description-concurrence-modal .user-information span{
  color: vt-color(dark-text);
  font-size: font-size(very-small);
}
@media (max-width: 1300px) {
  .description-concurrence-modal .modal-dialog {
    max-width: 98%;
  }
}
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
  .description-concurrence-modal .modal-dialog {
    max-width: 98%;
  }
}
.description-concurrence-modal .descriptionText .ql-toolbar {
    display: none;
}
.description-concurrence-modal .descriptionText .ql-container {
    border-top: 1px solid #ccc !important;
    word-break: break-word;
}
.description-concurrence-modal .concurrenceDescriptionEditor .btn__icon .btn__icon--primary {
    padding-top: 0;
}
.description-concurrence-modal .concurrenceDescriptionEditor .quill-editor {
    margin-top: 4px !important;
}
.description-concurrence-modal .descriptionText .ql-editor {
    height: calc(100vh - 310px);
}
.description-concurrence-modal .descriptionText.preview .ql-editor {
    height: calc(100vh - 353px);
}
.description-concurrence-modal .concurrenceDescriptionEditor .ql-editor {
    height: calc(100vh - 340px);
}
</style>

<style lang="scss" scoped>
.btn__outline-vt {
  border: 1px solid vt-color(primary);
  color: vt-color(primary);
  background-color: transparent;
}
.btn__outline-vt:hover {
  background-color: vt-color(primary);
  color: vt-color(white-light);
}
.title {
  color: vt-color(primary);
  font-size: font-size(normal);
}
.read-only-box {
  border: 1px solid rgba(0, 0, 0, 0.125);
  font-size: font-size(very-small);
  height: calc(100vh - 300px);
  overflow-y: auto;
  box-shadow: 0 2px 5px 0 #9c9c9c;
}
.description-concurrence-modal .btn {
  margin-right: 0 !important;
}

</style>
